/**
 * QuizPreviewV2 - Preview how a quiz renders for students
 * Shows all questions in read-only mode without grading or submission UI
 */
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Breadcrumb, Spinner } from 'react-bootstrap'
import * as constants from 'Constants'
import httpService from 'services/httpService'
import displayGlobalMessage from 'Utils/DisplayGlobalMessage'
import log from 'loglevel'

import QuestionCardWrapper from '../StudentSubmissionV2/QuestionCardWrapper'
import QuestionNavigator from '../StudentSubmissionV2/QuestionNavigator'

import submissionStyles from '../StudentSubmissionV2/StudentSubmissionV2.module.css'
import styles from './QuizPreviewV2.module.css'

const QuizPreviewV2 = ({ courseId: courseIdProp, overrides, embedded = false, notice }) => {
  const params = useParams()
  const courseId = courseIdProp || params.courseId
  const labId = params.labId
  const activityID = params.activityID

  const [activityData, setActivityData] = useState(null)
  const [loading, setLoading] = useState(true)
  const questionRefs = useRef([])

  const fetchActivityData = useCallback(async () => {
    if (!activityID) {
      setLoading(false)
      return
    }
    try {
      const url = `${constants.base_url}/lab/activity/${activityID}?type=other`
      const response = await httpService.get(url)
      const data = response.data?.other_activity
      if (data) {
        setActivityData(data)
      }
    } catch (error) {
      log.error('Error fetching activity data:', error)
      displayGlobalMessage('Error loading activity data', 'error')
    } finally {
      setLoading(false)
    }
  }, [activityID])

  useEffect(() => {
    fetchActivityData()
  }, [fetchActivityData])

  const scrollToQuestion = (index) => {
    if (questionRefs.current[index]) {
      questionRefs.current[index].scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  if (loading) {
    return (
      <div className={submissionStyles.container}>
        <div className={submissionStyles.loadingWrapper}>
          <Spinner animation="border" />
          <span>Loading preview...</span>
        </div>
      </div>
    )
  }

  const previewData = { ...(activityData || {}), ...(overrides || {}) }

  // Prepare questions with empty responses
  const questions = (previewData?.question_set || []).map(q => ({
    ...q,
    questionresponse_set: {}
  }))

  const totalMarks = questions.reduce((sum, q) => sum + (q.marks || 0), 0)
  const labTitle = previewData?.lab_data?.title || 'Lab'
  const activityTitle = previewData?.name || 'Activity'

  return (
    <div className={submissionStyles.pageContainer}>
      {/* Breadcrumb */}
      {!embedded && (
      <Breadcrumb className={submissionStyles.breadcrumb}>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/courseware/course/${courseId}/content/labs` }}>
          Labs
        </Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/courseware/course/${courseId}/content/labs/${labId}` }}>
          {labTitle}
        </Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/courseware/course/${courseId}/content/labs/${labId}/other/${activityID}` }}>
          {activityTitle}
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Preview</Breadcrumb.Item>
      </Breadcrumb>
      )}

      {notice && <div className={styles.previewNotice}>{notice}</div>}

      {/* Header */}
      <div className={submissionStyles.headerCard}>
        <div className={submissionStyles.headerLeft}>
          <h1>{activityTitle}</h1>
          <span className={submissionStyles.totalMarks}>Total Marks: {totalMarks}</span>
          {previewData?.description && (
            <div className={submissionStyles.activityDescription}>
              {typeof previewData.description === 'string'
                ? <span dangerouslySetInnerHTML={{ __html: previewData.description }} />
                : null}
            </div>
          )}
        </div>
        <div>
          <span className={styles.previewBadge}>Preview Mode</span>
        </div>
      </div>

      {/* Main Content */}
      <div className={submissionStyles.contentWrapper}>
        <div className={submissionStyles.mainContent}>
          {questions.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No questions have been added to this quiz yet.</p>
              <Link
                to={`/courseware/course/${courseId}/content/labs/${labId}/other/${activityID}/questions`}
                className={styles.addQuestionsLink}
              >
                Add Questions
              </Link>
            </div>
          ) : (
            questions.map((question, index) => (
              <div
                key={question.id || index}
                ref={(el) => (questionRefs.current[index] = el)}
                className={submissionStyles.questionWrapper}
              >
                <QuestionCardWrapper
                  question={question}
                  index={index}
                  courseId={courseId}
                  labId={labId}
                  activityData={previewData}
                  updateResponse={null}
                  shouldShowMarks={false}
                  isInstructor={true}
                  studentEmail={null}
                  isPreviewMode
                />
              </div>
            ))
          )}
        </div>

        {/* Sidebar */}
        {questions.length > 0 && (
          <div className={submissionStyles.sidebar}>
            <QuestionNavigator
              questions={questions}
              onQuestionClick={scrollToQuestion}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default QuizPreviewV2
